/**
 * Ведомость объёмов проекта (СМ-07) на вкладке «Сметы»: загрузка ведомости объёмов ПСД из Excel с привязкой к
 * комплекту и листам, строки из модели IFC, сколько позиций смет взяли объём из строки. Сумм здесь нет.
 */
import type { TakeoffImportResult, TakeoffList } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { takeoffsKey } from '../estimates/ItemTakeoff';

const qty = (v: number) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4 }).format(v);
const SHOW = 30;

export function TakeoffsPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const ref = useRef<HTMLInputElement>(null);
  const [setId, setSetId] = useState('');
  const [all, setAll] = useState(false);
  const [last, setLast] = useState<TakeoffImportResult | null>(null);
  const q = useQuery({
    queryKey: takeoffsKey(projectId),
    queryFn: () => api<TakeoffList>(`/projects/${projectId}/takeoffs`),
    retry: false,
  });
  const sets = useQuery({
    queryKey: ['psd-sets-pick', projectId],
    queryFn: () =>
      api<Array<{ id: string; code: string; name: string }>>('/psd/sets', { query: { projectId } }),
    enabled: Boolean(q.data?.canEdit),
    retry: false,
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      if (setId) form.append('setId', setId);
      form.append('file', file);
      return api<TakeoffImportResult>(`/projects/${projectId}/takeoffs/import`, { form });
    },
    onSuccess: (r) => {
      setLast(r);
      toast(`Добавлено строк ведомости: ${r.added}`);
      qc.invalidateQueries({ queryKey: takeoffsKey(projectId) });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/takeoffs/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: takeoffsKey(projectId) }),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  // Нет права на сметы проекта — панели нет
  if (q.isError || !q.data) return null;
  const { rows, canEdit } = q.data;
  if (!rows.length && !canEdit) return null;
  const used = rows.filter((r) => r.itemsCount > 0).length;
  const shown = all ? rows : rows.slice(0, SHOW);
  return (
    <Panel
      title="Ведомость объёмов"
      meta={
        rows.length
          ? `${rows.length} строк · объём взят в сметы из ${used}`
          : 'СМ-07: объёмы из ведомости ПСД и модели IFC'
      }
      actions={
        canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              className="min-h-[44px] md:min-h-0"
              aria-label="Комплект ПСД ведомости"
              value={setId}
              onChange={(e) => setSetId(e.target.value)}
            >
              <option value="">без комплекта ПСД</option>
              {(sets.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </Select>
            <input
              ref={ref}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              icon={<Upload className="size-4" />}
              loading={upload.isPending}
              onClick={() => ref.current?.click()}
            >
              Загрузить из Excel
            </Button>
          </div>
        ) : undefined
      }
      bodyClassName="p-0"
    >
      <ErrorBox error={upload.error} />
      {last && (last.skipped || last.issues.length) ? (
        <div className="border-b border-line bg-warning-soft px-5 py-3 text-[13px]">
          <p className="font-medium">Пропущено строк: {last.skipped}. Проверьте файл:</p>
          <ul className="mt-1 list-disc pl-5">
            {last.issues.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {rows.length ? (
        <ul className="divide-y divide-line">
          {shown.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-1 px-5 py-2 text-[14px] md:flex-row md:items-center md:gap-4"
            >
              <span className="num w-[130px] shrink-0 text-[12px] text-ink-2 [overflow-wrap:anywhere]">
                {r.source === 'ifc' ? (r.ifcType ?? 'IFC').replace(/^ifc/i, '') : (r.code ?? '—')}
              </span>
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                {r.name}
                {r.setId && r.setCode ? (
                  <Link
                    to="/psd/sets/$setId"
                    params={{ setId: r.setId }}
                    className="ml-2 inline-flex min-h-11 items-center text-[12px] text-accent-text underline md:min-h-0"
                  >
                    {r.setCode}
                    {r.sheetNumber ? `, лист ${r.sheetNumber}` : ''}
                  </Link>
                ) : null}
              </span>
              <span className="num shrink-0 md:w-[140px] md:text-right">
                {qty(r.quantity)} {r.unit}
              </span>
              <span className="shrink-0 text-[12px] text-ink-2 md:w-[110px]">
                {r.itemsCount ? `в сметах: ${r.itemsCount}` : 'не взята'}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Удалить строку «${r.name}»`}
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:text-danger disabled:opacity-40 md:size-8"
                  disabled={r.itemsCount > 0 || remove.isPending}
                  title={r.itemsCount > 0 ? 'Из строки взят объём позиции сметы' : 'Удалить строку'}
                  onClick={() => remove.mutate(r.id)}
                >
                  <Trash2 className="size-4" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-4 text-[14px] text-ink-2">
          Загрузите ведомость объёмов из Excel: строка заголовка с колонками «Наименование», «Ед. изм.» и
          «Количество»; «Шифр» и «Лист» — по желанию. Позиции смет проекта берут объём из строк ведомости, а
          проверка сметы покажет работы без строки и с другим объёмом.
        </p>
      )}
      {rows.length > SHOW ? (
        <div className="border-t border-line px-5 py-2">
          <Button variant="ghost" className="min-h-[44px] md:min-h-0" onClick={() => setAll((v) => !v)}>
            {all ? 'Свернуть' : `Показать все ${rows.length}`}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}
